import { useEffect, useRef } from 'react';
import type { OdooTask } from '@/types/odoo';
import { usePlanningStore } from '@/stores/planning';
import { displayProjectName } from '@/utils/format';

interface EasykitTourModalProps {
  tasks: OdooTask[];
  installerName: string;
  dateLabel: string;
  onClose: () => void;
}

export function EasykitTourModal({ tasks, installerName, dateLabel, onClose }: EasykitTourModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const projectNameMap = usePlanningStore((s) => s.projectNameMap);
  const soNameMap = usePlanningStore((s) => s.soNameMap);
  const werfMap = usePlanningStore((s) => s.werfMap);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  function handleBackdropClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (e.target === dialogRef.current) onClose();
  }

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={handleBackdropClick}
      className="backdrop:bg-black/40 bg-white rounded-xl shadow-2xl p-0 w-full max-w-lg mx-auto my-auto"
    >
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-td-navy flex items-center gap-2">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold">EASYKIT</span>
              EasyKit tour
            </h2>
            <p className="text-sm text-td-muted">{installerName} &middot; {dateLabel}</p>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-td-muted hover:text-td-navy transition-colors"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {tasks.length === 0 ? (
          <p className="text-sm text-td-muted py-4 text-center">Geen taken</p>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {tasks.map((task) => {
              const soId = task.sale_order_id ? task.sale_order_id[0] : 0;
              const projectName = projectNameMap.get(soId)
                || (task.project_id ? displayProjectName(task.project_id[1], { stripTemplateSuffix: true }) : 'Geen project');
              const soName = soNameMap.get(soId) || '';
              const werfId = task.x_studio_afleveradres ? task.x_studio_afleveradres[0] : 0;
              const werfLocation = werfMap.get(werfId) || '';

              return (
                <div
                  key={task.id}
                  className="border border-gray-200 rounded-lg p-3 bg-red-50/50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-td-navy truncate">
                        {projectName}
                        {soName && <span className="text-td-muted font-normal"> &middot; {soName}</span>}
                      </p>
                      {task.name && (
                        <p className="text-xs text-td-muted mt-0.5">{task.name}</p>
                      )}
                      {werfLocation && (
                        <p className="text-xs text-td-light mt-0.5 flex items-center gap-1">
                          <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {werfLocation}
                        </p>
                      )}
                    </div>
                  </div>
                  {task.description && typeof task.description === 'string' && (
                    <p className="text-xs text-td-muted mt-2 bg-white/60 rounded px-2 py-1 whitespace-pre-line line-clamp-3">
                      {task.description.replace(/<[^>]*>/g, '')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-gray-100 text-right">
          <span className="text-xs text-td-muted">{tasks.length} {tasks.length === 1 ? 'taak' : 'taken'}</span>
        </div>
      </div>
    </dialog>
  );
}
